import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import { BatchReportResponse, ReportStatus } from 'compliance-api';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useId, useMemo, useRef, useState } from 'react';
import Badge from '@/modules/common/components/Badge.tsx';
import { mapReportTypeToName } from '@/modules/common/components/ReportTypeSelect.tsx';
import { useDeleteReportMutation } from '@/modules/common/queries/reports.ts';
import BatchReportsModal from '@/modules/admin-dashboard/components/modals/BatchReportsModal.tsx';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import GenerateBatchReportsModal from '@/modules/admin-dashboard/components/modals/GenerateBatchReportsModal.tsx';
import {
  useDeleteReportsBatchMutation,
  useDownloadZipArchiveBatchReportsQuery,
  useGetBatchReportInfiniteQuery,
} from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  DisclosureListItem,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  Spinner,
  SubduedTextSet,
  TooltipNew,
} from 'ui';
import { useHiddenBadges } from 'ui/hooks';
import {
  AlertOrangeIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
  DownloadGreyIcon,
  TrashGrayIcon,
} from 'ui/icons';
import { fixNameEnum } from 'ui/utils';

interface BatchReportItemProps {
  batch: BatchReportResponse;
}

const BatchReportItem = ({ batch }: BatchReportItemProps) => {
  const hasArchiveBeenDownloaded = useRef(false);
  const [open, setOpen] = useState(false);
  const [archiveFetchEnabled, setArchiveFetchEnabled] = useState(false);
  const { openModal } = useContext(ModalProviderContext);
  const scrollableReportsContainerId = useId();
  const templatesBadgesRef = useRef<HTMLDivElement>(null);

  const hiddenTemplatesBadgesCount = useHiddenBadges(
    batch?.templates?.length || 0,
    templatesBadgesRef,
  );

  const { name, id } = batch;

  const {
    data: detailedData,
    isLoading: isDetailedDataLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetBatchReportInfiniteQuery({ id }, !open);

  const { mutateAsync: deleteReport, isLoading: isReportDeletion } = useDeleteReportMutation();

  const handleDownloadZip = useCallback(
    (zipArchiveData: string) => {
      if (!zipArchiveData) return;

      hasArchiveBeenDownloaded.current = true;
      const blob = new Blob([zipArchiveData], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `batch_report_${batch.name}.zip`;
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [batch.name],
  );

  const { data: zipArchiveData, isFetching: isZipArchiveFetching } =
    useDownloadZipArchiveBatchReportsQuery(batch.id, {
      disabled: !archiveFetchEnabled,
      onSuccess: handleDownloadZip,
    });

  const options = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: (
          <div>
            <span>Download ZIP</span>{' '}
            {!zipArchiveData && archiveFetchEnabled && <Spinner className="mr-0" />}
          </div>
        ),
        onClick: () => {
          if (!archiveFetchEnabled || !zipArchiveData) {
            setArchiveFetchEnabled(true);
          } else {
            handleDownloadZip(zipArchiveData);
          }
        },
        disabled: !batch.reports_quantity || (!zipArchiveData && archiveFetchEnabled),
      },
      {
        label: 'Generate',
        onClick: () => openModal(GenerateBatchReportsModal, { id, batchName: name }),
      },
      {
        label: 'Edit',
        onClick: () => openModal(BatchReportsModal, { initBatch: batch }),
      },
      {
        label: 'Delete',
        onClick: () =>
          openModal(DeleteModal, {
            mutation: useDeleteReportsBatchMutation,
            id: batch.id,
            name: batch.name,
            title: 'reports batch',
          }),
      },
    ];
  }, [archiveFetchEnabled, batch, handleDownloadZip, id, openModal, zipArchiveData]);

  const leftMainString = useMemo(
    () => (
      <div className="flex min-w-28 gap-2">
        <div>{name}</div>
        <div className="flex flex-wrap gap-2 w-full" ref={templatesBadgesRef}>
          {(batch?.templates || []).map((template, i, { length }) => (
            <React.Fragment key={i}>
              {length - i <= hiddenTemplatesBadgesCount ? null : (
                <Badge type="info" className="max-w-[95px] !block truncate leading-5">
                  {template.name}
                </Badge>
              )}
            </React.Fragment>
          ))}
          {!!hiddenTemplatesBadgesCount && (
            <TooltipNew
              text=""
              html={(batch?.templates || [])
                .slice(-hiddenTemplatesBadgesCount)
                .map(({ name }) => name)
                .join('<br/>')}
            >
              <Badge type="neutral">+ {hiddenTemplatesBadgesCount}</Badge>
            </TooltipNew>
          )}
        </div>
      </div>
    ),
    [batch?.templates, hiddenTemplatesBadgesCount, name],
  );

  const bottomContent = useMemo(() => {
    if (isDetailedDataLoading)
      return (
        <div className="w-full h-[100px] flex justify-center items-center border-t">
          <Spinner className="mr-0" />
        </div>
      );

    const reports = (detailedData?.pages || [])?.flatMap(({ reports }) => reports);

    if (!reports?.length) {
      return (
        <div className="w-full h-[100px] flex justify-center items-center border-t">
          <span className="font-semibold">No reports have been generated yet</span>
        </div>
      );
    }
    return (
      <div
        id={scrollableReportsContainerId}
        className="max-h-[300px] overflow-y-auto border-t pr-4 pl-10 py-2"
      >
        <InfiniteScroll
          hasMore={!!hasNextPage}
          next={fetchNextPage}
          dataLength={reports?.length}
          loader={
            <div className="flex p-4 justify-center items-center">
              <Spinner className="mr-0" />
            </div>
          }
          scrollableTarget={scrollableReportsContainerId}
        >
          {reports.map(report => (
            <div
              key={report.id}
              className="flex justify-between py-3 [&:not(:last-child)]:border-b"
            >
              <div>
                <div className="min-w-[160px] text-sm">{report.name}</div>
                <SubduedTextSet
                  set={[
                    `Created: ${dayjs(report.created_at).format('DD-MM-YYYY, HH:mm')}`,
                    mapReportTypeToName[report.type] || fixNameEnum(report.type),
                    report.format.toUpperCase(),
                  ]}
                />
              </div>
              <div className="flex gap-6 items-center">
                {report.status == ReportStatus.Completed ? (
                  <a href={report.url} target="_blank">
                    <img src={DownloadGreyIcon} className="cursor-pointer" alt="Download" />
                  </a>
                ) : report.status == ReportStatus.Generating ? (
                  <Spinner size="sm" className="mr-0" />
                ) : report.status == ReportStatus.Failed ? (
                  <TooltipNew text="Report generation failed">
                    <img src={AlertOrangeIcon} className="w-6 h-6" alt="alert" />
                  </TooltipNew>
                ) : (
                  <></>
                )}
                <button disabled={isReportDeletion}>
                  <img alt="delete" src={TrashGrayIcon} onClick={() => deleteReport(report.id)} />
                </button>
              </div>
            </div>
          ))}
        </InfiniteScroll>
      </div>
    );
  }, [
    deleteReport,
    detailedData?.pages,
    fetchNextPage,
    hasNextPage,
    isDetailedDataLoading,
    isReportDeletion,
    scrollableReportsContainerId,
  ]);

  return (
    <DisclosureListItem
      onClose={setOpen.bind(null, false)}
      onOpen={setOpen.bind(null, true)}
      rightContent={
        isZipArchiveFetching ? (
          <Spinner size="sm" className="mr-0" />
        ) : (
          <DropdownMenu
            items={options}
            placement="left"
            className="w-[180px] divide-white"
            button={
              <button>
                <img alt="options" src={DotsHorizontalIcon} />
              </button>
            }
          />
        )
      }
      leftContent={
        <img
          alt="open"
          width={24}
          src={ChevronDownIcon}
          className={classNames('shrink-0 select-none hover:opacity-80', { '-rotate-90': !open })}
        />
      }
      classes={{
        rightContent: '!hidden',
        rightContentUp: 'shrink-0',
        container: '!p-0',
        content: '!px-4 !py-3',
        leftContent: '!gap-4 w-[70%]',
        leftMainString: 'w-full',
      }}
      leftMainString={leftMainString}
      leftSubduedContentSet={[
        batch.reports_quantity ? `Reports: ${batch.reports_quantity}` : 'No reports',
        `Created at: ${dayjs(batch.created_at).format('DD-MM-YYYY, HH:mm')}`,
      ]}
      bottomCustomContent={bottomContent}
    />
  );
};

export default BatchReportItem;
